import { useRef, useEffect } from 'react';

import {
  Chart,
  LineController,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js';

import { FiscalYearBudget } from 'models/BudgetHistory';

interface ExpensesWidgetChartProps {
  fiscalYearsBudgets: FiscalYearBudget[];
}

function formatCurrency(amount: number, locale = 'fi-Fi') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'code',
  })
    .format(amount)
    .replace(/[a-z]/gi, '')
    .replace(/\s/g, ' ') /* replace non-breaking space with common space */
    .trim();
}

const ExpensesWidgetChart = ({
  fiscalYearsBudgets,
}: ExpensesWidgetChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { actualDataset, plannedDataset, labels } = fiscalYearsBudgets.reduce(
    (acc, next) => {
      if (!next.FiscalYear) return acc;
      acc.actualDataset.push(next.Actual);
      acc.plannedDataset.push(next.Budget);
      acc.labels.push(next.FiscalYear);

      return acc;
    },
    { actualDataset: [], plannedDataset: [], labels: [] } as {
      actualDataset: number[];
      plannedDataset: number[];
      labels: number[];
    }
  );

  const renderChart = () => {
    let context: CanvasRenderingContext2D | null = null;

    if (canvasRef.current) {
      context = canvasRef.current.getContext('2d');
    }

    if (context) {
      Chart.register(
        LineController,
        Tooltip,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement
      );

      new Chart(context, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              data: actualDataset,
              backgroundColor: '#218D7A',
              borderColor: '#218D7A',
              borderWidth: 2,
            },
            {
              data: plannedDataset,
              backgroundColor: '#224060',
              borderColor: '#224060',
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => {
                  return formatCurrency(context.parsed.y);
                },
              },
            },
          },
          scales: {
            yAxes: {
              type: 'linear',
              position: 'right',
              grid: {
                // display: false,
                borderDash: [6, 6],
                borderColor: '#707070',
              },
            },
            xAxes: {
              grid: {
                display: false,
              },
            },
          },
        },
      });
    }
  };

  useEffect(() => {
    renderChart();
  }, []);

  return (
    <div style={{ height: 130 }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ExpensesWidgetChart;
